import {Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild,ViewEncapsulation} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { AuthService } from 'src/app/services/auth/auth.service';
import { IRequestVaidacion2FA } from 'src/app/models/account/account.model';

import { MatIcon } from '@angular/material/icon';

type RegistroTwoFaTipo = 'Correo Electronico' | 'Telefono';
type RegistroTwoFaStep = 'informativo' | 'codigo';

interface RegistroTwoFaDialogData {
  tipo: RegistroTwoFaTipo;
  valor: string;
  usuario: string;
}

@Component({
    selector: 'app-registro-validador-dos-fa',
    templateUrl: './registro-validador-dos-fa.component.html',
    styleUrls: ['./registro-validador-dos-fa.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [MatIcon]
})
export class RegistroValidadorDosFaComponent implements OnInit, OnDestroy {
  @ViewChild('codigoRealInput') codigoRealInput?: ElementRef<HTMLInputElement>;

  readonly codigoLongitud = 6;

  formTwoFactorCode = new FormGroup({
    TwoFactorCode: new FormControl('', [
      Validators.required,
      Validators.minLength(6),
      Validators.maxLength(6),
      Validators.pattern(/^[0-9]{6}$/)
    ])
  });

  codigo: string[] = ['', '', '', '', '', ''];
  codigoEnfocado = false;

  step: RegistroTwoFaStep = 'informativo';

  procesando = false;
  codigoSolicitado = false;
  bloqueoCodigo = false;
  reenvio = true;

  codigoError = '';
  intervalo = 0;
  timer: any;

  maskedEmail = '';
  maskedPhone = '';
  tiempoBloqueo = 0;
  intentos = 0;

  constructor(
    public dialogRef: MatDialogRef<RegistroValidadorDosFaComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RegistroTwoFaDialogData,
    private readonly authService: AuthService
  ) {}

  ngOnInit(): void {
    this.solicitarCodigo();
  }

  ngOnDestroy(): void {
    this.limpiarTimer();
  }

  get esCorreo(): boolean {
    return this.data?.tipo === 'Correo Electronico';
  }

  get tituloModal(): string {
    return this.esCorreo ? 'Verificación de correo electrónico' : 'Verificación de teléfono';
  }

  get tituloInformativo(): string {
    return '¡Informativo!';
  }

  get destinoMostrado(): string {
    if (this.esCorreo) {
      return this.maskedEmail || this.data?.valor || '';
    }

    return this.maskedPhone || this.data?.valor || '';
  }

  get iconoPrincipal(): string {
    return this.esCorreo ? 'mail_outline' : 'phone_iphone';
  }

  get iconoBadge(): string {
    return this.esCorreo ? 'near_me' : 'lock';
  }

  get mensajeInformativo(): string {
    if (this.esCorreo) {
      return 'Le hemos enviado a su Correo Electrónico un código de autenticación de dos factores (2FA). Por favor revise su bandeja de entrada.';
    }

    return 'Le hemos enviado a su Teléfono un código de autenticación de dos factores (2FA). Por favor revise sus mensajes SMS.';
  }

  get codigoPlano(): string {
    return this.codigo.join('');
  }

  get codigoActivoIndex(): number {
    const longitud = this.codigoPlano.length;

    if (longitud >= this.codigoLongitud) {
      return this.codigoLongitud - 1;
    }

    return longitud;
  }

  aceptarInformativo(): void {
    if (this.codigoError && !this.codigoSolicitado) {
      this.closeDialog('Proceso Cancelado');
      return;
    }

    if (!this.codigoSolicitado || this.procesando) {
      return;
    }

    this.step = 'codigo';

    setTimeout(() => {
      this.enfocarCodigo();
    }, 80);
  }

  enfocarCodigo(): void {
    if (this.procesando || this.bloqueoCodigo) {
      return;
    }

    setTimeout(() => {
      const input = this.codigoRealInput?.nativeElement;

      if (!input) {
        return;
      }

      input.focus();
      this.colocarCursorAlFinal();
    });
  }

  posicionarCursorCodigo(): void {
    this.colocarCursorAlFinal();
  }

  manejarTeclaCodigo(event: KeyboardEvent): void {
    if (this.procesando || this.bloqueoCodigo) {
      event.preventDefault();
      return;
    }

    const tecla = event.key.toLowerCase();

    if ((event.ctrlKey || event.metaKey) && ['v', 'c', 'x', 'a'].includes(tecla)) {
      return;
    }

    const esNumero = /^\d$/.test(event.key);
    const teclasPermitidas = ['Backspace', 'Delete', 'Tab'];

    if (esNumero || teclasPermitidas.includes(event.key)) {
      return;
    }

    if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      this.colocarCursorAlFinal();
      return;
    }

    event.preventDefault();
  }

  capturarCodigo(event: Event): void {
    if (this.procesando || this.bloqueoCodigo) {
      return;
    }

    const input = event.target as HTMLInputElement;
    const valor = input.value.replace(/\D/g, '').slice(0, this.codigoLongitud);

    input.value = valor;

    this.actualizarCodigoVisual(valor);
    this.sincronizarCodigo(valor);

    setTimeout(() => {
      this.colocarCursorAlFinal();
    });
  }

  pegarCodigo(event: ClipboardEvent): void {
    event.preventDefault();

    if (this.procesando || this.bloqueoCodigo) {
      return;
    }

    const texto = event.clipboardData?.getData('text') || '';
    const valor = texto.replace(/\D/g, '').slice(0, this.codigoLongitud);

    this.establecerCodigoCompleto(valor);
  }

  ValidarCodigoTwoFactorCode(): void {
    if (this.formTwoFactorCode.invalid || this.procesando || this.bloqueoCodigo) {
      this.formTwoFactorCode.markAllAsTouched();

      if (!this.codigoError) {
        this.codigoError = 'Ingresa un código de 6 dígitos.';
      }

      return;
    }

    this.procesando = true;
    this.codigoError = '';

    const request: IRequestVaidacion2FA = {
      TipoEnvio: this.data.tipo,
      Valor: this.data.valor,
      TwoFactorCode: this.formTwoFactorCode.get('TwoFactorCode')?.value || ''
    };

    this.authService.ValidacionTwoFactorCode(request).pipe()
      .subscribe({
        next: (resp) => {
          this.procesando = false;

          if (resp?.validacion === true) {
            this.closeDialog(request);
            return;
          }

          this.marcarCodigoIncorrecto(
            'El código ingresado no es correcto. Verifícalo e intenta nuevamente.'
          );
        },
        error: (error) => {
          this.procesando = false;

          const mensaje = error?.error?.errorDescripcion ||
            'Ocurrió un error al validar el código.';

          this.marcarCodigoIncorrecto(mensaje);
        }
      });
  }

  resendCode(): void {
    if (this.procesando) {
      return;
    }

    this.solicitarCodigo();
  }

  closeDialog(data: any): void {
    this.limpiarTimer();
    this.dialogRef.close(data);
  }

  formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;

    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  private solicitarCodigo(): void {
    this.prepararSolicitudCodigo();

    const request: IRequestVaidacion2FA = {
      TipoEnvio: this.data.tipo,
      Valor: this.data.valor,
      Usuario: this.data.usuario,
      TwoFactorCode: ''
    };

    const solicitud$ = this.esCorreo
      ? this.authService.CodigoValidacionTwoFactorCodeEmail(request)
      : this.authService.CodigoValidacionTwoFactorCodeTelefono(request);

    solicitud$.pipe()
      .subscribe({
        next: (resp) => {
          this.procesarSolicitudExitosa(resp);
        },
        error: () => {
          this.procesarErrorSolicitud(
            this.esCorreo
              ? 'No fue posible enviar el código al correo electrónico. Intenta nuevamente.'
              : 'No fue posible enviar el código al teléfono. Intenta nuevamente.'
          );
        }
      });
  }

  private prepararSolicitudCodigo(): void {
    this.limpiarTimer();

    this.step = 'informativo';
    this.procesando = true;
    this.codigoSolicitado = false;
    this.bloqueoCodigo = false;
    this.reenvio = true;
    this.codigoError = '';
    this.intervalo = 0;
    this.codigoEnfocado = false;

    this.establecerCodigoCompleto('', false);
  }

  private procesarSolicitudExitosa(resp: any): void {
    if (resp?.datos?.Intervalo > 0) {
      this.procesando = false;
      this.codigoSolicitado = true;
      this.bloqueoCodigo = false;
      this.reenvio = true;

      this.intervalo = resp.datos.Intervalo;
      this.maskedEmail = resp.datos.MaskedEmail;
      this.maskedPhone = resp.datos.MaskedPhone;
      this.tiempoBloqueo = resp.datos.TiempoBloqueo;
      this.intentos = resp.datos.Intentos;

      this.startCountdown();
      return;
    }

    this.procesarErrorSolicitud(
      'No fue posible generar el código de verificación. Intenta nuevamente.'
    );
  }

  private procesarErrorSolicitud(mensaje: string): void {
    this.procesando = false;
    this.codigoSolicitado = false;
    this.bloqueoCodigo = true;
    this.reenvio = false;
    this.codigoError = mensaje;
  }

  private startCountdown(): void {
    this.limpiarTimer();

    this.timer = setInterval(() => {
      this.intervalo--;

      if (this.intervalo <= 0) {
        this.intervalo = 0;
        this.bloqueoCodigo = true;
        this.reenvio = false;
        this.codigoError = 'El código ha expirado. Solicita uno nuevo.';
        this.limpiarTimer();
      }
    }, 1000);
  }

  private establecerCodigoCompleto(valor: string, enfocar = true): void {
    const codigoLimpio = valor.replace(/\D/g, '').slice(0, this.codigoLongitud);
    const input = this.codigoRealInput?.nativeElement;

    if (input) {
      input.value = codigoLimpio;
    }

    this.actualizarCodigoVisual(codigoLimpio);
    this.sincronizarCodigo(codigoLimpio);

    if (enfocar) {
      this.enfocarCodigo();
    }
  }

  private actualizarCodigoVisual(valor: string): void {
    const digitos = valor.split('');

    this.codigo = Array.from({ length: this.codigoLongitud }, (_, index) => {
      return digitos[index] || '';
    });
  }

  private sincronizarCodigo(codigoCompleto: string = this.codigoPlano): void {
    const control = this.formTwoFactorCode.get('TwoFactorCode');

    control?.setValue(codigoCompleto);
    control?.markAsTouched();

    if (this.codigoError && codigoCompleto.length > 0 && !this.bloqueoCodigo) {
      this.codigoError = '';
    }
  }

  private marcarCodigoIncorrecto(mensaje: string): void {
    const control = this.formTwoFactorCode.get('TwoFactorCode');

    this.codigoError = mensaje;
    control?.setErrors({ codigoIncorrecto: true });
    control?.markAsTouched();

    this.enfocarCodigo();
  }

  private colocarCursorAlFinal(): void {
    const input = this.codigoRealInput?.nativeElement;

    if (!input) {
      return;
    }

    const posicion = input.value.length;

    try {
      input.setSelectionRange(posicion, posicion);
    } catch {
      // Algunos navegadores móviles pueden no permitir setSelectionRange en ciertos casos.
    }
  }

  private limpiarTimer(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}